import { isFiniteNumber, isString, isArray, orNull, sanitize } from './validate';

const PREFIX = 'karting-hotlap:';

export interface DayRecord {
  key: string;
  bestMs: number | null;
  attempts: number[];
  ghostB64: string | null;
  ghostMs: number | null;
  /** Cumulative sector times from the best lap, for live sector comparison. */
  bestSectors: number[] | null;
  /**
   * Fastest *individual* split per sector, across every attempt — not the
   * splits of the best lap. Summing them gives the optimal lap: a time you have
   * demonstrably already driven, just never all at once.
   */
  bestSplits: number[] | null;
  /** Best medal earned today. */
  medal: string | null;
  /** Ids of daily objectives completed. */
  objectives: string[];
  /** Best drill time per corner, keyed `${cornerNumber}:${drillMode}`. */
  cornerBests: Record<string, number>;
}

function emptyDay(key: string): DayRecord {
  return {
    key,
    bestMs: null,
    attempts: [],
    ghostB64: null,
    ghostMs: null,
    bestSectors: null,
    bestSplits: null,
    medal: null,
    objectives: [],
    cornerBests: {},
  };
}

/** Sector splits from cumulative sector times. */
export function splitsOf(cumulative: number[]): number[] {
  return cumulative.map((t, i) => t - (i > 0 ? cumulative[i - 1] : 0));
}

/** Merges a lap's splits into the running per-sector bests. */
export function mergeBestSplits(existing: number[] | null, splits: number[]): number[] {
  if (!existing || existing.length !== splits.length) return [...splits];
  return existing.map((best, i) => Math.min(best, splits[i]));
}

/** Sum of the best individual sectors — the optimal lap. */
export function optimalLap(splits: number[] | null): number | null {
  if (!splits || splits.length === 0) return null;
  return splits.reduce((a, b) => a + b, 0);
}

/** localStorage throws in private mode and when over quota; never let that end a run. */
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value);
  } catch {
    /* quota or private mode — the run still counts for this session */
  }
}

/**
 * Records shard by mode: assists change lap times, so a Sport best and an Arcade
 * best are not comparable and must not overwrite each other.
 */
export function loadDay(key: string, mode: string): DayRecord {
  const raw =
    safeGet(`day:${key}:${mode}`) ??
    // Records written before modes existed were all under today's Sport rules.
    (mode === 'sport' ? safeGet(`day:${key}`) : null);
  if (!raw) return emptyDay(key);
  try {
    const parsed = JSON.parse(raw) as DayRecord;
    if (parsed.key !== key) return emptyDay(key);
    // localStorage is user-editable and a record can outlive the build that
    // wrote it, so a field of the wrong type (e.g. `attempts: null`) must not
    // reach a consumer that assumes the declared type and throws on it
    // (`.push`/`.filter` on a `null`) — validate per field, default silently.
    return sanitize(emptyDay(key), parsed, {
      bestMs: orNull(isFiniteNumber),
      ghostB64: orNull(isString),
      ghostMs: orNull(isFiniteNumber),
      bestSectors: orNull(isArray),
      bestSplits: orNull(isArray),
      medal: orNull(isString),
    });
  } catch {
    return emptyDay(key);
  }
}

export function saveDay(record: DayRecord, mode: string): void {
  safeSet(`day:${record.key}:${mode}`, JSON.stringify(record));
}

export function loadLivery(): string {
  return safeGet('livery') ?? 'vantari';
}

export function saveLivery(id: string): void {
  safeSet('livery', id);
}

export function loadRealism(): string {
  return safeGet('realism') ?? 'arcade';
}

export function saveRealism(id: string): void {
  safeSet('realism', id);
}

export function loadAssist(): boolean {
  return safeGet('reference-line') === '1';
}

export function saveAssist(on: boolean): void {
  safeSet('reference-line', on ? '1' : '0');
}
